import { FC } from "react";
import { Paper, Typography, Box, useTheme } from "@mui/material";
import {
	Area,
	AreaChart,
	CartesianGrid,
	DotProps,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { ScoredSumPayload } from "../../../../../services/reports.service";

interface ScoredSumViewProps {
	payload: ScoredSumPayload;
}

export const SCORED_SUM_COLOR = "#1D9E75";

const DOT_RADIUS = 3;
const ACTIVE_DOT_RADIUS = 4;
const DOT_STROKE_WIDTH = 2;

const ScoredSumView: FC<ScoredSumViewProps> = ({ payload }) => {
	const theme = useTheme();
	const hasAnyData = payload.avgScoreByDay.some((d) => d.avgScore !== null);

	if (!hasAnyData) {
		return (
			<Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
				<Typography variant="body1" color="text.secondary">
					No submissions yet for this event in the selected window.
				</Typography>
			</Paper>
		);
	}

	// Recharts turns dots off by default on `<Area>` (`<Line>` enables them),
	// which leaves a day whose neighbours have no submissions invisible: there is
	// no line segment and the area polygon has no width, so the chart reads as
	// empty until the point is hovered. Drawing the markers keeps isolated days
	// visible and matches both the distress line chart in
	// `IndependentRatingsView` and `SowsProgressionChart`.
	//
	// The marker is hollow — `background.paper` is the card surface the chart
	// sits on, so it reads as a ring in the series colour, and the radius grows
	// while hovered, mirroring `SowsProgressionChart`.
	const dot: DotProps = {
		r: DOT_RADIUS,
		fill: theme.palette.background.paper,
		stroke: SCORED_SUM_COLOR,
		strokeWidth: DOT_STROKE_WIDTH,
	};

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Box sx={{ width: "100%", height: 320 }}>
				<ResponsiveContainer width="100%" height="100%">
					<AreaChart
						data={payload.avgScoreByDay}
						margin={{ top: 12, right: 24, left: 12, bottom: 8 }}
					>
						<defs>
							<linearGradient id="scoredSumFill" x1="0" y1="0" x2="0" y2="1">
								<stop
									offset="0%"
									stopColor={SCORED_SUM_COLOR}
									stopOpacity={0.45}
								/>
								<stop
									offset="100%"
									stopColor={SCORED_SUM_COLOR}
									stopOpacity={0.05}
								/>
							</linearGradient>
						</defs>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis
							dataKey="day"
							type="number"
							domain={[1, 14]}
							ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]}
						/>
						<YAxis domain={[payload.scoreMin, payload.scoreMax]} />
						<Tooltip
							formatter={(value) =>
								typeof value === "number" ? value.toFixed(1) : "—"
							}
							labelFormatter={(label) => `Day ${label}`}
						/>
						<Area
							type="monotone"
							dataKey="avgScore"
							stroke={SCORED_SUM_COLOR}
							strokeWidth={2}
							fill="url(#scoredSumFill)"
							dot={dot}
							activeDot={{ ...dot, r: ACTIVE_DOT_RADIUS }}
							connectNulls={false}
							isAnimationActive={false}
						/>
					</AreaChart>
				</ResponsiveContainer>
			</Box>
		</Paper>
	);
};

export default ScoredSumView;
